import { ref, readonly } from "vue";
import type { Ref } from "vue";
import { AuthApiService } from "@/api/generated/services/AuthApiService";
import { handleGeneratedApiError } from "@/api/transport/configureGeneratedClient";

interface AuthState {
  userId: string | null;
  email: string | null;
  displayName: string | null;
  permissions: Set<string>;
  loaded: boolean;
}

const state = ref<AuthState>({
  userId: null,
  email: null,
  displayName: null,
  permissions: new Set(),
  loaded: false,
});

let authPromise: Promise<void> | null = null;

async function loadAuth(): Promise<void> {
  if (authPromise) {
    return authPromise;
  }

  authPromise = (async () => {
    try {
      const data = await AuthApiService.getApiAuthMe();
      state.value = {
        userId: data.userId,
        email: data.email,
        displayName: data.displayName,
        permissions: new Set(data.permissions ?? []),
        loaded: true,
      };
    } catch (error) {
      if (!isIntentionalAnonymousAuthResponse(error)) {
        try {
          await handleGeneratedApiError(error);
        } catch {
          // Preserve anonymous fallback behavior for auth bootstrap failures.
        }
      }

      state.value = { userId: null, email: null, displayName: null, permissions: new Set(), loaded: true };
    }
  })();

  return authPromise;
}

function waitForAuth(): Promise<void> {
  return authPromise ?? loadAuth();
}

function isIntentionalAnonymousAuthResponse(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }

  const apiError = error as { status?: unknown; request?: { method?: unknown; url?: unknown } };
  return apiError.status === 401
    && apiError.request?.method === "GET"
    && apiError.request.url === "/api/auth/me";
}

function hasPermission(permission: string): boolean {
  return state.value.permissions.has(permission);
}

function hasAnyPermission(...permissions: string[]): boolean {
  return permissions.some((p) => state.value.permissions.has(p));
}

export function useAuthStore() {
  return {
    state: readonly(state) as Readonly<Ref<AuthState>>,
    loadAuth,
    waitForAuth,
    hasPermission,
    hasAnyPermission,
  };
}
